import React, { useEffect, useMemo, useState } from 'react';
import { api, apiError } from '../../api/client';
import { Button } from '../ui';
import { agentLabel } from '../../lib/format';
import './PromptPanel.css';

function entryLabel(entry) {
  if (entry.key.startsWith('role/') || entry.key.startsWith('agent/')) return agentLabel(entry.key.split('/')[1]);
  const summary = (entry.text || '').split('\n').find(line => line.trim()) || 'Дополнительная инструкция';
  return summary.length > 90 ? summary.slice(0, 87) + '…' : summary;
}

export function lineDiff(before, after) {
  const a = (before || '').split('\n'), b = (after || '').split('\n');
  if (a.length * b.length > 1000000) return [{ type: 'removed', text: before }, { type: 'added', text: after }];
  const lengths = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i--) for (let j = b.length - 1; j >= 0; j--)
    lengths[i][j] = a[i] === b[j] ? 1 + lengths[i + 1][j + 1] : Math.max(lengths[i + 1][j], lengths[i][j + 1]);
  const result = [];
  let i = 0, j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) { result.push({ type: 'same', text: a[i++] }); j++; }
    else if (i < a.length && (j === b.length || lengths[i + 1][j] >= lengths[i][j + 1])) result.push({ type: 'removed', text: a[i++] });
    else result.push({ type: 'added', text: b[j++] });
  }
  return result;
}

function Difference({ before, after }) {
  return <pre className="prompt-diff" aria-label="Различия инструкций">{lineDiff(before, after).map((line, index) =>
    <span className={`prompt-diff-${line.type}`} key={index}>{line.type === 'added' ? '+ ' : line.type === 'removed' ? '− ' : '  '}{line.text}{'\n'}</span>)}</pre>;
}

export default function PromptPanel() {
  const [config, setConfig] = useState(null), [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [selected, setSelected] = useState(''), [query, setQuery] = useState('');
  const [draft, setDraft] = useState({}), [comparison, setComparison] = useState(null);
  const [preview, setPreview] = useState(null), [error, setError] = useState(''), [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const load = async (preserve = false) => {
    try {
      const [next, versions] = await Promise.all([api.getPrompts(), api.getPromptVersions()]);
      setConfig(next); setHistory(versions.items); setHistoryTotal(versions.total || versions.items.length); setSelected(value => value || next.entries[0]?.key || '');
      if (!preserve) setDraft({});
      setError('');
    } catch (failure) { setError(apiError(failure)); }
  };
  useEffect(() => { load(); }, []);
  const entry = config?.entries.find(item => item.key === selected);
  const text = entry ? Object.prototype.hasOwnProperty.call(draft, selected) ? draft[selected] ?? entry.default : entry.text : '';
  const previous = comparison?.entries.find(item => item.key === selected);
  const visible = useMemo(() => config?.entries.filter(item => `${item.key} ${item.source} ${entryLabel(item)}`.toLowerCase().includes(query.toLowerCase())) || [], [config, query]);
  useEffect(() => {
    if (!visible.some(item => item.key === selected)) { setSelected(visible[0]?.key || ''); setPreview(null); }
  }, [visible, selected]);
  const perform = async operation => {
    setBusy(true); setError(''); setNotice('');
    try {
      const next = await operation();
      setConfig(next); setDraft({}); setPreview(null);
      const versions = await api.getPromptVersions();
      setHistory(versions.items); setHistoryTotal(versions.total || versions.items.length);
      setNotice('Версия сохранена. Она будет действовать со следующего запуска исследования.');
    } catch (failure) { setError(apiError(failure)); }
    finally { setBusy(false); }
  };
  return <section className="card prompt-panel" aria-label="Инструкции всех ролей">
    <h2 className="card-title">Инструкции всех ролей</h2>
    <p>Изменения применяются к новым запускам. Текущие исследования продолжают работать со своей версией.</p>
    {error && <div role="alert" className="alert alert-error">{error} <Button onClick={() => load(true)}>Загрузить актуальную версию, сохранив черновик</Button></div>}
    {notice && <p role="status">{notice}</p>}
    {!config ? <p>Загрузка…</p> : <>
      <p>Конфигурация №{config.revision}</p>
      {config.references?.length > 0 && <details><summary>Инструкции методик и скиллов</summary>
        <p>Методики сохраняют собственные версии. Их инструкции редактируются в каталоге скиллов.</p>
        <ul>{config.references.map(item => <li key={item.id}><a href="/admin/skills">{item.title}</a> · версия {item.current_version_id || 'не опубликована'}</li>)}</ul>
      </details>}
      <label>Найти роль или инструкцию<input aria-label="Найти роль или инструкцию" className="form-control" value={query} onChange={event => setQuery(event.target.value)} /></label>
      <label>Часть инструкций<select aria-label="Часть инструкций" className="form-control" value={selected} onChange={event => { setSelected(event.target.value); setPreview(null); }}>
        {visible.map(item => <option value={item.key} key={item.key}>{entryLabel(item)}{item.default_changed ? ' · обновлён исходный шаблон' : ''}</option>)}
      </select></label>
      {entry && <>
        {entry.default_changed && <p role="status">Встроенный шаблон обновился. Сравните изменения перед сохранением своего текста.</p>}
        {entry.variables.length > 0 && <p>Обязательные переменные: {entry.variables.map(value => `{{${value}}}`).join(', ')}</p>}
        <label>Текст инструкции<textarea aria-label="Текст инструкции" className="form-control prompt-editor" value={text || ''} onChange={event => setDraft(value => ({ ...value, [selected]: event.target.value }))} /></label>
        <div className="flex gap-2 prompt-actions">
          <Button className="btn btn-primary" disabled={busy || !Object.keys(draft).length} onClick={() => perform(() => api.savePrompts({ base_bundle_revision: config.revision, edits: draft }))}>Сохранить версию</Button>
          {entry.default_changed && <Button disabled={busy} onClick={() => perform(() => api.savePrompts({ base_bundle_revision: config.revision, edits: { ...draft, [selected]: text } }))}>Сохранить мой текст</Button>}
          {entry.default != null && <Button disabled={busy} onClick={() => setDraft(value => ({ ...value, [selected]: null }))}>Использовать встроенный текст</Button>}
          <Button disabled={busy} onClick={async () => { try { setPreview(await api.previewPrompts({ base_bundle_revision: config.revision, edits: draft })); } catch (failure) { setError(apiError(failure)); } }}>Состав промпта</Button>
        </div>
        <details><summary>Изменения относительно сохранённого текста</summary><Difference before={entry.text} after={text} /></details>
        {entry.default_changed && <>
          <details><summary>Что изменилось во встроенном шаблоне</summary><Difference before={entry.base_default} after={entry.default} /></details>
          <details><summary>Мой текст и новый встроенный шаблон</summary><Difference before={entry.default} after={text} /></details>
        </>}
      </>}
      <label>Сравнить с историей<select aria-label="Сравнить с историей" className="form-control" value={comparison?.revision || ''} onChange={async event => {
        if (!event.target.value) { setComparison(null); return; }
        try { setComparison(await api.getPromptVersion(event.target.value)); } catch (failure) { setError(apiError(failure)); }
      }}><option value="">Выберите версию</option>{history.map(item => <option key={item.revision} value={item.revision}>№{item.revision} · {new Date(item.created_at).toLocaleString('ru')} · {item.reason}</option>)}</select></label>
      {history.length < historyTotal && <Button disabled={busy} onClick={async () => {
        setBusy(true);
        try { const next = await api.getPromptVersions({ offset: history.length }); setHistory(value => [...value, ...next.items]); }
        catch (failure) { setError(apiError(failure)); }
        finally { setBusy(false); }
      }}>Загрузить более ранние версии</Button>}
      {comparison && <><Difference before={previous?.text || ''} after={text} />
        <Button disabled={busy || Object.keys(draft).length > 0} onClick={() => perform(() => api.restorePrompts({ base_bundle_revision: config.revision, revision: comparison.revision }))}>Восстановить конфигурацию №{comparison.revision} новой версией</Button>
        {Object.keys(draft).length > 0 && <p>Для восстановления сначала сохраните или сбросьте черновик.</p>}</>}
      {Object.keys(draft).length > 0 && <Button onClick={() => { setDraft({}); setPreview(null); }}>Сбросить черновик</Button>}
      {preview && <div role="region" aria-label="Предпросмотр состава"><h3>Предпросмотр шаблона</h3>
        <p>{preview.note}</p><pre className="prompt-preview">{preview.entries.find(item => item.key === selected)?.text}</pre>
        <p>При выполнении добавляются: {preview.dynamic_sections.join('; ')}.</p><p>Источник: {entry?.source}</p></div>}
    </>}
  </section>;
}
